<script setup lang="ts">
import { AlertMessage, ContentComp, DropdownSelect, ListComp, ToolbarComp } from '@/components';
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';
import { useDialog, useKeyboardListener, useTaskLoader } from '@/composeables';
import { TimepenaltyDao } from '@/dao';
import { DropdownItemButton, type SelectLine } from '@/objects';
import {
    ContestantAndRaceContestantTime,
    RaceContestant,
    Timepenalty,
    TimepenaltyRequest,
    type RaceContestantDetails,
} from '@/objects/generated';
import type ListCompExpose from '@/objects/ListCompExpose';
import { useEventCommonStoreRef, useGuiStore, useToastStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import { propertyof } from '@/utilities';
import { useTransactions } from '@/composeables/transactions/transactions';
import { ThreadSleep } from '@/utilities/utils';
import ContestantAddTimepenalty from './ContestantAddTimepenalty.vue';
import { TimeSpan } from '@/objects/TimeSpan';
import { addTimespanToTime } from '@/utilities/dateutils';

const listComp = ref<ListCompExpose<ContestantAndRaceContestantTime> | null>(null);

const contestant = defineModel<RaceContestantDetails>({ required: true });

const { AddLoader, LoadState } = useTaskLoader();

const { Races } = useEventCommonStoreRef();

const data = ref<Timepenalty[]>([]);

// Set the initial value for the contestant
const firstRaceContestant: RaceContestant | null = contestant.value.RaceContestants.length > 0 ? contestant.value.RaceContestants[0] : null;

// The currently selected race contestant
const selectedRaceContestantId = ref<number | null>(firstRaceContestant?.ID ?? null);

// We create the datasource for the dropdown with the race contestants
const computedRacecontestantsAsSelectline = computed(() => {
    return contestant.value.RaceContestants.map((rc) => {
        return {
            value: rc.ID,
            text: Races.value.find((x) => x.ID == rc.RaceID)?.Name ?? '',
        };
    });
});

/**
 * Load the data for the view. We load all the data from the backend
 */
const LoadData = () => {
    AddLoader(async () => {
        data.value = (
            (await TimepenaltyDao.GetTimePenaltiesForContestant(contestant.value.EventID, contestant.value.ContestantID)) ?? []
        ).filter((x) => !x.Deleted);
        listComp.value?.dataUpdated();
    }, '');
};

LoadData();

/**
 * Callback triggered when the save button in the edit time modal is clicked
 * @param request
 */
const saveTimepenalty = (request: TimepenaltyRequest) => {
    if (request.StationRoleID == null || request.Reason?.trim() == '') {
        useToastStore().addError('contestanttimepenalty.missingstationroleorreason');
        return;
    }
    // Add a new time
    AddLoader(async () => {
        // await useTransactions().AddPenalty(new TimepenaltyRequest(JSON.parse(JSON.stringify(request))));

        await useTransactions().AddPenalty(request);
        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async
        LoadData();
    }, '');
};

/**
 * Map the columns to the grid
 * @param columns
 */
const handleCustomColumns = (columns: GridColumnOptions<Timepenalty>[]) => {
    // // We change the cellrenderer for the time column for our custom component that wraps the DateComp with a edit button
    let column = columns.find((c) => c.dataField == propertyof<Timepenalty>('Penalty'));

    if (column != null) {
        column.aggregate = {
            type: 'sum',
            aggregate: () => {
                // We return the sum of all the penalties of the rows that has the correct selectedRaceContestant
                const ts = data.value
                    .filter((x) => x.RaceContestantID == selectedRaceContestantId.value)
                    .reduce((acc, val) => addTimespanToTime(acc, val.Penalty), TimeSpan.FromSeconds(0));

                return ts ?? TimeSpan.FromSeconds(0);
            },
        };
    }

    return columns;
};

/**
 * Add a new time for the selected contestant
 */
const addPenalty = () => {
    if (selectedRaceContestantId.value == null) {
        throw new Error('No valid racecontestant selected');
    }

    let raceContestant = contestant.value.RaceContestants.find((rc) => rc.ID == selectedRaceContestantId.value);
    // We assume it is finish
    let request = TimepenaltyRequest.FromJsValues({
        RaceContestantID: raceContestant?.ID,
        ContestantID: raceContestant?.ContestantID,
        EventID: raceContestant?.EventID,
        RaceID: raceContestant?.RaceID,
        ReasonID: 0,
        Penalty: TimeSpan.FromSeconds(0),
    });
    let valuewrapper = { request: request };

    useDialog().SaveCancel({
        title: 'contestanttimepenalty.addnewtitle',
        body: ContestantAddTimepenalty,
        saveCallback: async () => {
            saveTimepenalty(request);
            return true;
        },
        componentProps: {
            value: { wrapper: valuewrapper },
        },
        options: {
            Size: 'lg',
            DisableAnimation: true,
        },
    });
};

/**
 * Disable the selected tuids
 * @param tuids
 */
const disableTimes = (tuids: string[]) => {
    AddLoader(async () => {
        await useTransactions().DisableTransactions(tuids);

        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async

        LoadData();
    }, '');
};

/**
 * Helper function to get the selected TUIDs
 */
const getSelectedTUIDs = () =>
    listComp.value
        ?.getSelectedRows()
        ?.map((x) => x.TUID)
        ?.filter((x) => x != null) ?? [];

// The toolbar actions
const toolbarActions = [
    DropdownItemButton.create({
        buttonType: 'Primary',
        translationkey: 'contestanttimepenalty.addnew',
        title: 'contestanttimepenalty.addnewtitle',
        icon: 'fa-plus',
        action: addPenalty,
    }),
    DropdownItemButton.create({
        buttonType: 'Secondary',
        translationkey: 'contestanttimepenalty.delete',
        title: 'contestanttimepenalty.deletetitle',
        icon: 'fa-xmark',
        action: () => disableTimes(getSelectedTUIDs()),
    }),
];

const toolbarRightActions = [
    DropdownItemButton.create({
        buttonType: 'Secondary',
        translationkey: 'general.refresh',
        icon: 'fa-sync',
        action: () => LoadData(),
    }),
];

/**
 * The row class rules for the grid
 * We want to show the current active times as Green and the times that are not active as red. Disabled times we show as grey
 */
const rowClassRules: IGridRowCssRule<Timepenalty>[] = [
    {
        className: 'grid-row-success',
        func: (data: Timepenalty) => data.RaceContestantID == selectedRaceContestantId.value,
    },
    {
        className: 'grid-row-warning',
        func: (data: Timepenalty) => data.RaceContestantID != selectedRaceContestantId.value,
    },
];

/**
 * Callback when the visibility of the component changes
 * @param isVisible
 */
const onIsVisible = (isVisible: boolean) => {
    // We add a temporary keyboard shortcut for CTRL+INSERT to add a new time
    if (isVisible) {
        useKeyboardListener().addKeyboardShortcut({ alias: 'contestanttimepenaltyaddnew', on: 'CTRL+INSERT', action: addPenalty });
    } else {
        // We make sure we remove the keyboard shortcut when the component is not visible
        useKeyboardListener().removeKeyboardShortcut('contestanttimepenaltyaddnew');
    }
};

/**
 * Make sure we set the selectedRaceContestant when the selectedRaceContestantId changes
 */
watch(contestant.value.RaceContestants, (nv, ov) => {
    if (selectedRaceContestantId.value == null && nv?.length > 0) {
        selectedRaceContestantId.value = nv[0].ID;
    }
});

// Just trigger a data updated when we change the race contestants. We do this so we also run the row rules
watch(selectedRaceContestantId, () => listComp.value?.dataUpdated());
</script>
<template>
    <ContentComp
        v-if="contestant && selectedRaceContestantId != null"
        class="p-0"
        :height="50"
        :load-state="LoadState"
        :show-as-modal="true"
        @is-visible="onIsVisible"
    >
        <AlertMessage
            type="info"
            :dismissable="true"
        >
            <span v-html="$tm('contestanttimepenalty.info', '', true)"></span>
        </AlertMessage>

        <ToolbarComp>
            <template #first>
                <DropdownSelect
                    v-if="computedRacecontestantsAsSelectline.length > 1"
                    :data="computedRacecontestantsAsSelectline"
                    v-model="selectedRaceContestantId"
                />
                <ButtonGroup :actions="toolbarActions" />
            </template>
            <template #last>
                <ButtonGroup :actions="toolbarRightActions" />
            </template>
        </ToolbarComp>
        <ListComp
            ref="listComp"
            class="overflow-auto h-100"
            v-model="data"
            :gui="useGuiStore().Timepenalty"
            :select-all="true"
            :hide-toolbar="true"
            :readonly="true"
            :row-class-rules="rowClassRules"
            :custom-columns="handleCustomColumns"
            resize-strategy="sizeColumnsToFit"
            :hide-statusbar="true"
        />
    </ContentComp>
</template>
